import classNames from 'classnames'
import { Button, Checkbox, Container, Header, Icon } from '@boltenergy-be/design-system'
import ProducerCard from 'components/producer-card/ProducerCard'
import ProducerMap from 'components/producers/producer-map/ProducerMap'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next/pages'
import { EnergyType, Producer } from 'types/producer'
import { handleContactMeClick } from 'utils/helpers'
import useWindowSize from 'hooks/useWindowSize'
import { getCapacityState, getEnergyTypeIcon } from 'utils/producers'
import { sortAtBottomIds } from './ProducersOverview.constants'
import styles from './ProducersOverview.module.scss'
import { MobileView, ProducersOverviewProps } from './types'
import { filterProducers } from './utils'
import { useRouter } from 'next/router'
import { routes } from 'utils/routes'
import { CustomerFlows, InputGroupKeys, SimulationFieldKeys } from 'store/customer/enums'
import { SimulationSteps } from 'features/simulation/types'
import { goToStep, updateInput } from 'store/customer/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import useSelectInputGroup from 'hooks/useSelectInputGroup'
import { useGetLanguage } from 'hooks/useGetLanguage'
import { resetDataLayer, trackGTMEvent } from 'utils/tracking'
import Link from 'components/link/Link'
import LatLngBoundsLiteral = google.maps.LatLngBoundsLiteral
import { DEFAULT_PRODUCT, Product, SIMULATION_TYPES, SimulationType } from '@boltenergy-be/shared'
import { getTotalElectricityVolumeForMeterType } from 'utils/usage'

const ProducersOverview = ({ producers }: ProducersOverviewProps) => {
  // Redux store
  const { didSimulation, isLoading } = useStoreSelector((store) => store.customer.flows.simulation)
  const propositions = useStoreSelector((store) => store.customer.propositions)
  const simulation = useSelectInputGroup(InputGroupKeys.SIMULATION)
  const { needsGas } = useSelectInputGroup(InputGroupKeys.DELIVERY_ADDRESS)
  const { noOptinOne, availableProducts } = useStoreSelector((store) => store.marketing)
  const dispatch = useStoreDispatch()

  // i18n
  const { t } = useTranslation(['producers', 'common', 'producer'])
  const language = useGetLanguage()

  // Router
  const router = useRouter()

  // Window Size
  const { isSmaller } = useWindowSize(1120)
  const isDesktop = !isSmaller

  // Local state
  const [activePositionIndex, setActivePositionIndex] = useState<number | undefined>(undefined)
  const [activeProducerId, setActiveProducerId] = useState<string | undefined>(undefined)
  const [bounds, setBounds] = useState<LatLngBoundsLiteral | undefined>(undefined)
  const [energyTypes, setEnergyTypes] = useState<EnergyType[]>(Object.values(EnergyType))
  const [mobileView, setMobileView] = useState<MobileView>(MobileView.LIST)
  const [popupPositionIndex, setPopupPositionIndex] = useState<number | undefined>(undefined)
  const [popupProducerId, setPopupProducerId] = useState<string | undefined>(undefined)
  const [hideSoldOut, setHideSoldOut] = useState<boolean>(false)

  // Refs
  const producersRef = useRef<HTMLUListElement | null>(null)

  // Constants
  const isMobileMapView = mobileView === MobileView.MAP && !isDesktop

  // Producers
  const sortedProducers = useMemo(
    () =>
      [...producers].sort((a, b) => {
        const aCapacity = getCapacityState(a, simulation, didSimulation)
        const bCapacity = getCapacityState(b, simulation, didSimulation)

        // if available
        if (!aCapacity.soldOut && !aCapacity.usedEnergy && bCapacity.usedEnergy === 100 && bCapacity.soldOut) {
          return -1
        }

        // if A is reserved and B is available and A is not a SortAtBottomId
        if (!aCapacity.soldOut && aCapacity.usedEnergy === 100 && !bCapacity.soldOut && bCapacity.usedEnergy < 100) {
          return 1
        }

        // if sold out
        if (aCapacity.soldOut && !bCapacity.soldOut) {
          return 1
        }

        // sort inside sold out
        // TODO should not be hardcoded but managed by internal CMS/Salesforce
        if (aCapacity.soldOut && bCapacity.soldOut) {
          if (sortAtBottomIds.includes(Number(a.id))) {
            return 1
          }

          if (sortAtBottomIds.includes(Number(b.id))) {
            return -1
          }
        }

        return 0
      }),
    [didSimulation, simulation, producers]
  )
  const filteredProducers = useMemo(
    () => filterProducers(sortedProducers, energyTypes, bounds, hideSoldOut),
    [sortedProducers, energyTypes, bounds, hideSoldOut]
  )

  /**
   * Scrolls to top and blocks scrolling when opening map view on smaller screens
   */
  useEffect(() => {
    if (mobileView === MobileView.MAP && !isDesktop) {
      document.body.classList.add('no-scroll')
      window.scrollTo(0, 0)
    } else {
      document.body.classList.remove('no-scroll')
    }
  }, [isDesktop, mobileView])

  /**
   * Toggles back to list view when going from smaller screen to desktop
   */
  useEffect(() => {
    if (isDesktop) {
      setMobileView(MobileView.LIST)
    }
  }, [isDesktop])

  /**
   * Handles the energy filter click
   * Toggles the energy type in the state
   *
   * @param energyType
   */
  const handleEnergyFilterClick = (energyType: EnergyType) => {
    let selectedEnergyTypes = [...energyTypes]

    if (selectedEnergyTypes.includes(energyType)) {
      selectedEnergyTypes = selectedEnergyTypes.filter((et) => et !== energyType)
    } else {
      selectedEnergyTypes.push(energyType)
    }

    setEnergyTypes(selectedEnergyTypes)

    // Track the filter usage
    resetDataLayer()
    trackGTMEvent('energy_filter', {
      energy_filter: selectedEnergyTypes
    })
  }

  return (
    <main className={styles['producers-overview']}>
      <Header as="section" docked title={t(($) => $.producers.title)} color="dark">
        <p>{t(($) => $.producers.subTitle)}</p>

        <Link representation="button" variant="layered" href={routes(language).becomeProducer}>
          {t(($) => $.producers.becomeProducer)}
        </Link>
      </Header>

      <Container
        as="section"
        color="light"
        classnames={{
          container: classNames(styles['controls-container'], { [styles['fixed']]: isMobileMapView }),
          content: styles['controls-content']
        }}
      >
        <div className={styles['controls']}>
          <div className={classNames('flex-row gap-200', styles['energy-type-filter'])}>
            {Object.keys(EnergyType).map((key: EnergyType) => (
              <Button
                key={key}
                onClick={() => handleEnergyFilterClick(EnergyType[key])}
                variant={energyTypes.includes(EnergyType[key]) ? 'primary' : 'secondary'}
                size="small"
                className={styles['energy-type-button']}
                leadingIcon={getEnergyTypeIcon(EnergyType[key])}
                title={t(($) => $.energyTypes[EnergyType[key]], { ns: 'producer' })}
              />
            ))}
          </div>

          {/* MOBILE CONTROLS */}
          <div className={styles['view-toggle']}>
            <Button
              isFullwidth
              size="small"
              variant="secondary"
              onClick={() => setMobileView(mobileView === MobileView.LIST ? MobileView.MAP : MobileView.LIST)}
              leadingIcon={mobileView === MobileView.LIST ? 'location' : 'list'}
            >
              {mobileView === MobileView.LIST ? t(($) => $.map) : t(($) => $.list)}
            </Button>
          </div>

          {/* HIDE SOLD OUT */}
          <Checkbox
            checked={hideSoldOut}
            id="hide-sold-out"
            label={t(($) => $.map.hideSoldOut, { ns: 'producer' })}
            name="hide-sold-out"
            onChange={() => setHideSoldOut(!hideSoldOut)}
          />
        </div>

        {/* USAGE */}
        {didSimulation && (
          <div>
            <p>{t(($) => $.basedOnYourUsage)}:</p>

            {didSimulation && !isLoading && (
              <div className="flex-row gap-400">
                <div className="flex-row gap-100">
                  <Icon name="electricity" />
                  {getTotalElectricityVolumeForMeterType(simulation.meterType, simulation.usage)}
                  &nbsp;kWh
                </div>

                {needsGas && (
                  <div className="flex-row gap-100">
                    <Icon name="gas" />
                    {simulation.usage.gas || 0}&nbsp;kWh
                  </div>
                )}

                <Button
                  representation="link"
                  onClick={() => {
                    dispatch(
                      goToStep({
                        flow: CustomerFlows.SIMULATION,
                        step: SimulationSteps.POSTAL_CODE
                      })
                    )
                    /** TODO: check flow (feels a bit odd to go to registration flow instead of back to producersOverview)
                     * solutions:
                     * 1. pass finish url to query or store so simulation knows where to go back to
                     * 2. open enhanced CustomerMeterUsageModal here with gas+el/el toggle & estimated values
                     **/
                    return router.push(routes(language).simulation)
                  }}
                >
                  {t(($) => $.editUsage, { ns: 'producer' })}
                </Button>
              </div>
            )}

            {didSimulation && isLoading && (
              <p className={classNames(styles.number, 'mb-none', 'mt-none')}>{t(($) => $.calculatingYourPrice)}</p>
            )}
          </div>
        )}

        {/* 'NORMAL' PRICE SIMULATION BUTTON - for offline sales simulation type is not 'Pricing Simulation' */}
        {!([SIMULATION_TYPES.express, SIMULATION_TYPES.pricingSimulation] as SimulationType[]).includes(
          simulation.chosenSimulationType
        ) && (
          <Button
            onClick={() => {
              dispatch(
                updateInput({
                  group: InputGroupKeys.SIMULATION,
                  key: SimulationFieldKeys.CHOSEN_SIMULATION_TYPE,
                  value: SIMULATION_TYPES.pricingSimulation
                })
              )
              return router.push(routes(language).simulation)
            }}
          >
            {t(($) => $.priceSimulation, { ns: 'common' })}
          </Button>
        )}

        {/* CONTACT ME BUTTON - for offline sales when no_optin_one url param was given */}
        {didSimulation && noOptinOne && (
          <Button
            onClick={() => {
              const { region } = simulation
              const availableProduct =
                region && availableProducts && region in availableProducts && (availableProducts[region]?.[0] || DEFAULT_PRODUCT)
              const simulatedProducts = Object.keys(propositions.simulatedPropositions ?? {}) as Product[]
              void handleContactMeClick(
                propositions.simulateSpecificProduct || simulatedProducts.length === 1
                  ? propositions.simulateSpecificProduct || simulatedProducts[0]
                  : availableProduct || DEFAULT_PRODUCT
              )
            }}
          >
            {t(($) => $.sendProposal, { ns: 'common' })}
          </Button>
        )}
      </Container>

      <Container
        as="section"
        classnames={{
          container: classNames(styles['producers-container'], { [styles['mobile-map-view']]: isMobileMapView }),
          content: styles['producers-content']
        }}
      >
        {filteredProducers.length === 0 ? (
          <p>{t(($) => $.noProducersFound)}</p>
        ) : (
          <ul className={styles.producers} ref={producersRef}>
            {filteredProducers.map((producer, index) => (
              <li key={`producer-${producer.slug}-${index}`}>
                <ProducerCard
                  producer={producer}
                  onMouseEnter={(producer) => {
                    setActivePositionIndex(index)
                    setActiveProducerId(producer.id)
                  }}
                  onMouseLeave={() => setActiveProducerId(undefined)}
                />
              </li>
            ))}
          </ul>
        )}

        {/* PRODUCER MAP */}
        <div className={styles['map-container']}>
          <div className={styles.map}>
            <ProducerMap
              enablePanning={mobileView === MobileView.MAP}
              onBoundsChange={(bounds: LatLngBoundsLiteral) => !popupProducerId && setBounds(bounds)}
              onClick={(producer?: Producer, index?: number) => {
                setPopupProducerId(producer ? producer.id : undefined)
                setPopupPositionIndex(typeof index !== 'undefined' ? index : undefined)
              }}
              onMouseEnter={(producer: Producer, index: number) => {
                setActiveProducerId(producer.id)
                setActivePositionIndex(index)
              }}
              onMouseLeave={() => {
                setActiveProducerId(undefined)
                setActivePositionIndex(undefined)
              }}
              onZoomChanged={() => {
                if (!isMobileMapView) {
                  producersRef.current?.scrollIntoView()
                }
              }}
              producers={filteredProducers}
              {...{ activePositionIndex, activeProducerId, popupPositionIndex, popupProducerId }}
            />
          </div>
        </div>
      </Container>
    </main>
  )
}

export default ProducersOverview
